/**
 * Pure geometry for the docking "directional pad" — the 5-icon compass shown
 * while dragging a tab over another tabset's content area (top/bottom/
 * left/right split, or center to merge into that tabset).
 *
 * DOM-agnostic, no side effects — the actual overlay creation, positioning
 * and drag-event interception happens in dockable-layout.tsx.
 */

export type SplitDirection = "top" | "bottom" | "left" | "right" | "center"

/** Natural size (px) of the compass artwork at 1:1 — matches the SVG viewBox baked into flexlayout-overrides.css. */
export const COMPASS_WIDTH = 115
export const COMPASS_HEIGHT = 114

interface HitZone {
  x: number
  y: number
  w: number
  h: number
}

// Each arm's icon bounding box, lifted from the compass SVG's per-arm
// `<g transform="translate(...)">` offsets (icons are ~27.3 x 27.6 local
// units). Padded out a few px so the icon is an easier drag target than its
// exact rendered bounds.
const ICON_W = 27.3
const ICON_H = 27.6
const HIT_PADDING = 5

const RAW_ZONES: Record<SplitDirection, { x: number; y: number }> = {
  center: { x: 43.5, y: 43 },
  top: { x: 43.5, y: 3 },
  bottom: { x: 43.5, y: 83 },
  left: { x: 3, y: 43 },
  right: { x: 84, y: 43 },
}

const ZONES: Record<SplitDirection, HitZone> = Object.fromEntries(
  (Object.entries(RAW_ZONES) as [SplitDirection, { x: number; y: number }][]).map(([dir, p]) => [
    dir,
    { x: p.x - HIT_PADDING, y: p.y - HIT_PADDING, w: ICON_W + HIT_PADDING * 2, h: ICON_H + HIT_PADDING * 2 },
  ]),
) as Record<SplitDirection, HitZone>

/** Center point of each direction's icon, in pad-local coordinates (0,0 = pad's top-left corner). */
export const ZONE_CENTERS: Record<SplitDirection, { x: number; y: number }> = Object.fromEntries(
  (Object.entries(RAW_ZONES) as [SplitDirection, { x: number; y: number }][]).map(([dir, p]) => [
    dir,
    { x: p.x + ICON_W / 2, y: p.y + ICON_H / 2 },
  ]),
) as Record<SplitDirection, { x: number; y: number }>

/**
 * Which of the compass's 5 icon hit-zones (if any) contains a point given in
 * pad-local coordinates. Returns null outside all zones — a drag is only
 * actionable directly over an icon, never just "somewhere toward that edge"
 * of the panel, so dropping away from the pad (including fully out at the
 * panel's own left/right/top/bottom edge) is always a no-op.
 */
export function detectDirection(localX: number, localY: number): SplitDirection | null {
  for (const dir of Object.keys(ZONES) as SplitDirection[]) {
    const z = ZONES[dir]
    if (localX >= z.x && localX <= z.x + z.w && localY >= z.y && localY <= z.y + z.h) {
      return dir
    }
  }
  return null
}

export interface RectLike {
  x: number
  y: number
  width: number
  height: number
}

/** Mirrors flexlayout's own DockLocation.getDockRect — the preview rect for a given split direction. */
export function dockRectFor(direction: SplitDirection, panel: RectLike): RectLike {
  switch (direction) {
    case "top":
      return { x: panel.x, y: panel.y, width: panel.width, height: panel.height / 2 }
    case "bottom":
      return { x: panel.x, y: panel.y + panel.height / 2, width: panel.width, height: panel.height / 2 }
    case "left":
      return { x: panel.x, y: panel.y, width: panel.width / 2, height: panel.height }
    case "right":
      return { x: panel.x + panel.width / 2, y: panel.y, width: panel.width / 2, height: panel.height }
    case "center":
      return { ...panel }
  }
}
